import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { LockKey, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/auth'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. Please request a new link.');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="reset-password-page">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-20 fade-up">
        {done ? (
          <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-8 text-center">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={30} weight="fill" className="text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl text-foreground mb-2">Password updated!</h1>
            <p className="text-sm text-muted-foreground" data-testid="reset-done">Taking you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-card border border-border/70 rounded-2xl shadow-soft p-8">
            <div className="w-12 h-12 bg-secondary rounded-2xl flex items-center justify-center mb-4">
              <LockKey size={24} className="text-primary" />
            </div>
            <h1 className="font-heading text-2xl text-foreground mb-2">Choose a new password</h1>
            <p className="text-sm text-muted-foreground mb-6">Minimum 8 characters. You'll be signed out of all other devices.</p>
            <label className="block text-sm font-medium text-foreground mb-1.5">New password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 mb-4"
              data-testid="reset-password-input" />
            <label className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 mb-4"
              data-testid="reset-confirm-input" />
            {error && <p className="text-xs text-red-500 mb-4" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy}
              className="w-full bg-primary text-primary-foreground rounded-xl h-11 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
              data-testid="reset-submit">
              {busy ? 'Saving…' : 'Set new password'}
            </button>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Link expired? <Link to="/forgot-password" className="text-primary font-medium hover:underline">Request a new one</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
